<template>
    <k-page>
        <template v-slot:header>
            <profile-header/>
        </template>
        <k-container size="lg" class="pb-20">

          <div style="display: flex; justify-content: space-between; align-items: center;">
                      <a @click="$router.go(-1)" class="text-primary-500 mr-2 my-5 block">
                          &#8592; {{ $t("goBack") }}
                      </a>
                  <a-button type="primary" v-if="!$root.$data.investorProfile.vwoFeatures.goBackToReact.goBackToReactButtonHide" @click="this.$goBackToNewPlatform">Go back to new UI</a-button>
            </div>
            <k-alert class="mb-6" v-if="!$root.$data.investorProfile.active">
                <template slot="text">{{ $t("accountNotActive") }}</template>
                <template slot="description">
                    {{ $t("accountNotActiveDesc") }}
                </template>
            </k-alert>
            <div class="my-5 grid md:grid-cols-2 gap-5">
                <div>
                    <div class="mb-5" v-if="tranche && tranche.pictures && tranche.pictures.length > 0">
                        <img class="rounded object-scale-down"
                             :src="`/api/guest/download/${tranche.pictures[0]}`"
                             onerror="this.src=`https://via.placeholder.com/800`"
                        >
                    </div>
                    <p class="leading-7" style="white-space: pre-line;"
                       v-if="!loading && tranche.details.summary && tranche.details.summary.shortDescription">{{
                            tranche.details.summary.shortDescription
                        }}</p>
                </div>
                <div>
                    <tranche-details
                        v-if="!loading"
                        :tranche="tranche"
                        :borrower="borrower"
                    />
                    <div style="height: 100%">
                        <tranche-reservation-details
                            class="mt-5"
                            v-if="!loading && reservation"
                            :tranche-uuid="trancheUuid"
                            :reservation="reservation"
                            :currencySymbol="tranche.currencySymbol"
                            @on-update="reservationUpdated"
                        />
                        <tranche-investment-details
                            class="mt-5"
                            v-if="!loading"
                            :investment="investment"
                            :currencySymbol="tranche.currencySymbol"
                            @on-cancel="unsubscribe"
                        />
                        <tranche-invest-form
                            class="mt-5"
                            v-if="!loading"
                            :offer="offer"
                            :tranche="tranche"
                            :investment="investment"
                            :investorActive="$root.$data.investorProfile.active"
                            ref="investForm"
                            @on-inquiry="submitInquiry"
                            @on-commit="commitFunds"
                            @on-investment-terms="openTerms"
                            @on-letter-of-waiver="openLetterOfWaiver"
                        />

                    </div>
                </div>
            </div>

            <div v-if="!loading">
                <a-affix :offset-top="50">
                    <div class="bg-white" style="padding:20px">
                        <ul class="flex items-stretch gap-2 cursor-pointer py-4" style="overflow-x: auto">
                            <li class="k-tab-profile" @click="goTo('#borrower')">Borrower</li>
                            <li class="k-tab-profile" @click="goTo('#key-financials')" v-if="keyFinancialsAvailable">Key
                                Financials
                            </li>
                            <li class="k-tab-profile" @click="goTo('#deal-structure')">Deal Structure</li>
                            <li class="k-tab-profile" @click="goTo('#capital-calls')" v-if="tranche.capitalCallEnabled">
                                Capital Calls
                            </li>
                            <li class="k-tab-profile" @click="goTo('#payments')">Payments</li>
                            <li class="k-tab-profile" @click="goTo('#additional-materials')"
                                v-if="additionalMaterialsAvailable">Additional Materials
                            </li>
                            <li class="k-tab-profile" @click="goTo('#documents')">Documents</li>
                        </ul>
                    </div>
                </a-affix>
                <section id="borrower">
                    <k-panel>
                        <template v-slot:header>
                            <k-panel-header>Business Description of the Borrower</k-panel-header>
                        </template>
                        <div class="my-5 grid md:grid-cols-2 gap-5">
                            <div slot="left"><p class="leading-7" style="white-space: pre-line;">{{
                                    borrower.description
                                }}</p></div>
                            <div slot="right">
                                <tranche-borrower
                                    :borrower="borrower"
                                    :trancheType="tranche.trancheType"/>
                            </div>
                        </div>
                    </k-panel>
                </section>
                <section id="key-financials" v-if="keyFinancialsAvailable">
                    <k-panel>
                        <template v-slot:header>
                            <k-panel-header>Key Financials</k-panel-header>
                        </template>
                        <div class="mb-5 overflow-x-auto">
                            <table class="table-auto bg-white mt-5" v-if="keyFinancialsTable">
                                <thead>
                                <tr class="text-sm bg-gray-100">
                                    <th class="border px-4 py-2 text-center"
                                        v-for="(header, index) in keyFinancialsTable.headers" :key="index">{{ header }}
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(row, index) in keyFinancialsTable.rows" :key="index">
                                    <td class="border px-4 py-2 text-center" v-for="(cell, index2) in row"
                                        :key="index2">{{ cell }}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                            <div v-if="tranche.details.keyFinancialsIframeCode">
                                <div class="iframe-video-wrapper" v-html="tranche.details.keyFinancialsIframeCode"/>
                            </div>
                        </div>
                    </k-panel>
                </section>
                <section id="deal-structure">
                    <tranche-deal-structure :tranche="tranche"></tranche-deal-structure>
                </section>
                <section id="capital-calls" v-if="tranche.capitalCallEnabled">
                    <tranche-capital-call
                        v-if="!loading"
                        :tranche="tranche"
                        :investment="investment"
                        :requests="capitalCallRequests"
                        @on-request="refreshCapitalCallRequests"
                        @on-cancel="refreshCapitalCallRequests"
                        class="mb-6"
                    />
                </section>
                <section id="payments">
                    <tranche-outstanding-payments
                        v-if="!loading"
                        :tranche="tranche"
                    />
                </section>
                <section id="additional-materials" v-if="additionalMaterialsAvailable">
                    <k-panel>
                        <template v-slot:header>
                            <k-panel-header>Additional Materials</k-panel-header>
                        </template>
                        <div>
                            <div class="mt-10 mb-2 font-bold">{{ tranche.details.summary.embeddedVideoTitle }}</div>
                            <div class="iframe-video-wrapper" v-html="tranche.details.summary.embeddedVideoIframeCode"/>
                        </div>
                    </k-panel>
                </section>
                <section id="documents">
                    <k-panel>
                        <template v-slot:header>
                            <k-panel-header>Documents</k-panel-header>
                        </template>
                        <div class="mb-2 text-primary-500 flex justify-between max-w-sm" v-for="doc in attachments"
                             :key="doc.fileReference">
                            <a :href="`/api/guest/view/${doc.fileReference}`" target="_blank"
                            >{{ doc.fileName | documentName }}</a>
                            <a class="grid content-center" :href="`/api/guest/download/${doc.fileReference}`"
                               target="_blank"
                            >
                                <a-icon type="download"/>
                            </a>
                        </div>
                    </k-panel>
                </section>

            </div>
        </k-container>
    </k-page>
</template>

<script>
import ProfileHeader from '@investor/components/profile/ProfileHeader';
import TrancheInvestForm from '@product-common/components/tranches2/TrancheInvestForm';
import TrancheInvestmentDetails from '@product-common/components/tranches2/TrancheInvestmentDetails';
import TrancheDetails from '@product-common/components/tranches2/TrancheDetails';
import TrancheBorrower from '@product-common/components/tranches2/TrancheBorrower';
import TrancheOutstandingPayments from '@product-common/components/tranches2/TrancheOutstandingPayments';
import KTabs from "@common/components/tabs/KTabs";
import KTab from "@common/components/tabs/KTab";
import TrancheSummary from "@product-common/components/tranches2/TrancheSummary";
import TrancheDealStructure from "@product-common/components/tranches2/TrancheDealStructure";
import TrancheMarket from "@product-common/components/tranches2/TrancheMarket";
import TrancheCapitalCall from "../../../common/components/tranches2/TrancheCapitalCall";
import Papa from 'papaparse';
import TrancheReservationDetails from "../../../common/components/tranches2/TrancheReservationDetails.vue";

export default {
    components: {
        TrancheReservationDetails,
        ProfileHeader,
        TrancheInvestForm,
        TrancheInvestmentDetails,
        TrancheDetails,
        TrancheBorrower,
        TrancheOutstandingPayments,
        KTabs,
        KTab,
        TrancheSummary,
        TrancheDealStructure,
        TrancheMarket,
        TrancheCapitalCall,
    },
    data() {
        return {
            loading: true,
            trancheUuid: null,
            offer: null,
            investment: {},
            reservation: null,
            tranche: {},
            borrower: {},
            attachments: [],
            capitalCallRequests: [],
        };
    },
    created() {
        this.trancheUuid = this.$route.params.trancheUuid;
    },
    async mounted() {
        await this.refresh();
    },
    methods: {
        async refresh() {
            this.loading = true;
            try {
                this.offer = null;
                const resp = await this.$post('tranche', {trancheUuid: this.trancheUuid});
                this.tranche = resp.tranche;
                this.investment = resp.investment;
                this.reservation = resp.reservation;
                this.borrower = resp.borrower;
                this.attachments = resp.attachments;
                await this.refreshCapitalCallRequests();
            } finally {
                this.loading = false;
            }
        },
        async submitInquiry(inquiryForm) {
            let timeoutId1;
             timeoutId1 = setTimeout(() => {
                    this.$message.info({
                        content: "We're processing your investment, which may take a little longer than usual due to its substantial size. Thank you for your patience.",
                        duration: 5
                    });
                }, 7000);
            try{
            let resp = await this.$post('tranche/subscription-offer', inquiryForm);
            this.offer = resp.offer;
            this.$trackGAEvent('investing', 'createSubmit');
            }catch (error) {
                   const statusCode = error.response && error.response.status;
                   if (statusCode === 520 || statusCode === 524 || statusCode === 504) {
                   this.$message.error("We might be facing a technical issue. Please try clicking 'Invest' again. If it fails more than 5 times, contact our sales team for assistance: sales@kilde.sg or +65 6950 7768.");
                   }
             }
            finally{
              clearTimeout(timeoutId1);
            }
        },
        async refreshCapitalCallRequests() {
            try {
                this.capitalCallRequests = (await this.$post('tranche/capital-call-requests', {trancheUuid: this.trancheUuid})).capitalCallRequests;
            } catch (e) {
                console.error('Failed to get capital call requests', e)
            }
        },
        async submitCapitalCallRequest() {
            await this.refreshCapitalCallRequests()
        },
        async commitFunds(commitForm) {
        let timeoutId1;
           timeoutId1 = setTimeout(() => {
                            this.$message.info({
                                content: "We're processing your investment, which may take a little longer than usual due to its substantial size. Thank you for your patience.",
                                duration: 5
                            });
                        }, 7000);
            try {
                   await this.$post('tranche/subscribe', commitForm);
                   await this.refresh();
                   await this.$fetchInvestorProfile();
                   this.$message.success("Congratulations! Your subscription is successful.");
                   this.$trackGAEvent('investing', 'createSubmitSuccess');
                   this.$trackGADataLayerEvent('createSubmit', this.$root.$data.investorProfile.number);
              } catch (error) {
                const statusCode = error.response && error.response.status;
              if (statusCode === 520 || statusCode === 524 || statusCode === 504) {
               this.$message.error("We're processing your investment, which may take a little longer than usual due to its substantial size. Please refresh the page in a few minutes for an update or contact sales@kilde.sg or +6569507768.");
               }
             }finally{
                  clearTimeout(timeoutId1);
             }
        },
        async unsubscribe() {
            await this.$post('tranche/unsubscribe', {trancheUuid: this.trancheUuid});
            await this.refresh();
            await this.$fetchInvestorProfile();
        },
        async reservationUpdated(){
            await this.refresh();
            await this.$fetchInvestorProfile();
        },
        async openTerms(form) {
            window.open(`/api/investor/tranche/note-of-acceptance-preview?trancheUuid=${form.trancheUuid}&amount=${form.amount}`, '_blank');
        },
        async openLetterOfWaiver(form) {
            window.open(`/api/investor/tranche/letter-ov-waiver-preview?trancheUuid=${form.trancheUuid}`, '_blank');
        },
        goTo(section) {
            const element = this.$el.querySelector(section);
            if (element) {
                const yOffset = -100;
                const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
                window.scrollTo({top: y, behavior: "smooth"});
            }
        }
    },
    computed: {
        additionalMaterialsAvailable() {
            return this.tranche.details.summary && this.tranche.details.summary.embeddedVideoTitle && this.tranche.details.summary.embeddedVideoIframeCode;
        },
        keyFinancialsAvailable() {
            return this.tranche && (this.tranche.details.keyFinancialsCsv || this.tranche.details.keyFinancialsIframeCode);
        },
        keyFinancialsTable() {
            const parsed = Papa.parse(this.tranche.details.keyFinancialsCsv, {header: true});

            return {
                headers: parsed.meta.fields,
                rows: parsed.data.map(row => Object.values(row))
            }
        }
    }
};
</script>

<style scoped>
#key-financials th:first-child {
    width: 200px;
}

#key-financials th:not(:first-child) {
    width: 150px;
}
</style>
